import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, LogOut } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useAuth } from '@/shared/hooks/useAuth';
import { usePermissionCheck } from '@/shared/hooks/usePermissionCheck';
import { authService } from '@/shared/lib/auth';
import { administrationApi } from '@/features/administration/api/administrationApi';
import { accountsApi } from '@/features/accounts/api/accountsApi';
import { getErrorMessage } from '@/shared/lib/error-handler';
import { toast } from '@/shared/hooks/use-toast';

// Barra global exibida enquanto o usuario (platform admin ou conta-pai) esta
// com a visao de uma conta assumida. Sair da visao re-emite a sessao com os
// claims originais e recarrega a aplicacao.
export function ActingViewBanner() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { isPlatformAdmin } = usePermissionCheck();
  const [leaving, setLeaving] = useState(false);

  if (!user?.actingTenantId) return null;

  const handleExit = async () => {
    setLeaving(true);
    try {
      // Conta-pai sai pelo endpoint de contas e volta para a raiz: o endpoint
      // de plataforma responderia 403 e /administracao/tenants e rota do Root.
      const platform = isPlatformAdmin();
      const auth = platform
        ? await administrationApi.releaseView()
        : await accountsApi.releaseView();
      authService.completeLogin(auth);
      window.location.assign(platform ? '/administracao/tenants' : '/');
    } catch (e) {
      setLeaving(false);
      toast({
        title: t('actingViewBanner.exitErrorTitle'),
        description: getErrorMessage(e, t('actingViewBanner.exitErrorFallback')),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="md:sticky md:top-0 z-50 flex items-center justify-center gap-3 border-b border-primary/30 bg-primary/15 px-4 py-1.5 text-sm">
      <Eye className="h-4 w-4 text-primary" />
      <span>
        {t('actingViewBanner.viewingAs')} <span className="font-semibold">{user.actingTenantName ?? t('actingViewBanner.accountFallback')}</span>
      </span>
      <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={handleExit} disabled={leaving}>
        <LogOut className="h-3.5 w-3.5" />
        {leaving ? t('actingViewBanner.exiting') : t('actingViewBanner.exitButton')}
      </Button>
    </div>
  );
}
